import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  CONVERSATION_TITLE_MAX,
  TAB_SLUGS,
  ConversationReportReason,
  type ConversationFollowState,
  type ConversationLocations,
  type ConversationMessageView,
  type ConversationThreadView,
} from '@scoutaround/script-breakdown-shared';

import { api, ApiError, type ProjectPerson } from '../../lib/api.js';
import { useCan } from '../../lib/permissions.js';
import { Composer, mentionablesFrom, referenceablesFrom } from './Composer.js';
import { PlacePreview, ScriptLocationPreview } from './LocationPreview.js';
import { MessageBody } from './MessageBody.js';
import { STAFF_ACCESS_NOTICE } from './NewThreadForm.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import '../../styles/conversations.css';
import {
  newRequestKey,
  personName,
  useConversationLocations,
  useMyUserId,
  usePeople,
} from './hooks.js';

const POLL_MS = 6_000;

/** One reference, open in the References library (`?ref=`). A project id is a valid address. */
export function referenceInLibraryPath(projectId: string, referenceId: string): string {
  return `/projects/${projectId}/${TAB_SLUGS.REFERENCES}?ref=${encodeURIComponent(referenceId)}`;
}

const REPORT_LABELS: Readonly<Record<ConversationReportReason, string>> = Object.freeze({
  HARASSMENT: 'Harassment or abuse',
  SPAM: 'Spam',
  CONFIDENTIAL: 'Shares something it should not',
  OTHER: 'Something else',
});

const ANCHOR_STATE_NOTE: Readonly<Record<string, string>> = Object.freeze({
  REMOVED: 'No longer in the production',
  EARLIER_DRAFT: 'From an earlier draft',
});

function when(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A system line, in words. */
function systemText(m: ConversationMessageView, people: readonly ProjectPerson[] | undefined, me: number | null): string {
  const actor = personName(people, m.authorUserId, me);
  const subjectId = typeof m.systemPayload?.userId === 'number' ? m.systemPayload.userId : null;
  const subjectIsMe = subjectId !== null && subjectId === me;
  // The reader is "You" opening a sentence and "you" inside one, and "you own".
  const subject = subjectId === null ? 'Someone' : personName(people, subjectId, me);
  const object = subjectId === null ? 'someone' : subjectIsMe ? 'you' : subject;
  switch (m.kind) {
    case 'PARTICIPANT_ADDED':
      return `${actor} added ${object}.`;
    case 'PARTICIPANT_REMOVED':
      return `${actor} removed ${object}.`;
    case 'PARTICIPANT_LEFT':
      return `${subject} left.`;
    case 'OWNER_CHANGED':
      return subjectIsMe ? 'You now own this conversation.' : `${subject} now owns this conversation.`;
    case 'RESOLVED':
      return `${actor} marked it resolved.`;
    case 'REOPENED':
      return `${actor} reopened it.`;
    case 'RENAMED':
      return `${actor} renamed it.`;
    default:
      return '';
  }
}

/** Two letters for the avatar square: a name's first and last word. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter((w) => /\p{L}/u.test(w));
  const first = words[0]?.[0] ?? '?';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

function placeholderFor(m: ConversationMessageView): string {
  if (m.redacted || m.hiddenBy === 'STAFF') return 'Removed by ScoutAround.';
  if (m.hiddenBy === 'AUTHOR') return 'Message withdrawn.';
  return 'Hidden by the production.';
}

/**
 * ONE CONVERSATION (ADR 0021): its messages, polled every 6 s while the tab is
 * visible; the reader's cursor, sent once the newest message is on screen; and
 * everything a reader may do to it, offered only where the server would allow it.
 * A conversation the reader may no longer see answers 404 and is shown as gone.
 *
 * TWO LAYOUTS (redesign of 2026-10-30). In the Conversations tab (`pane`) it is a
 * messaging pane: the header and its controls on top, the messages scrolling
 * between, the composer always at the bottom, and the view kept on the newest
 * message unless the reader has scrolled back. In the drawer (`flow`) it is one
 * column that scrolls with the drawer. Secondary actions — rename, leave, and per
 * message withdraw, report and hide — sit in menus in both.
 */
export function ThreadView({
  projectId,
  threadId,
  onClosed,
  onShown,
  layout = 'flow',
}: {
  projectId: string;
  threadId: string;
  onClosed?: () => void;
  /** Once, when the conversation (or its "not available" answer) is on screen. */
  onShown?: () => void;
  layout?: 'pane' | 'flow';
}) {
  const queryClient = useQueryClient();
  const me = useMyUserId();
  const canPost = useCan('CONVERSATION_POST');
  const canManage = useCan('CONVERSATION_MANAGE');
  const canModerate = useCan('CONVERSATION_MODERATE');

  const thread = useQuery({
    queryKey: ['conversation', projectId, threadId],
    queryFn: () => api.conversation(projectId, threadId),
    refetchInterval: 20_000,
    retry: false,
  });
  const live = useQuery({
    queryKey: ['conversation-messages', projectId, threadId],
    queryFn: () => api.conversationMessages(projectId, threadId),
    refetchInterval: POLL_MS,
    retry: false,
  });
  const people = usePeople(projectId, thread.data?.anchorType);
  const locations = useConversationLocations(projectId);
  const referenceable = useMemo(() => referenceablesFrom(locations.data), [locations.data]);

  const [older, setOlder] = useState<ConversationMessageView[]>([]);
  const [olderDone, setOlderDone] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);

  const messages = useMemo(() => {
    const bySeq = new Map<number, ConversationMessageView>();
    for (const m of [...older, ...(live.data?.messages ?? [])]) bySeq.set(m.seq, m);
    return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
  }, [older, live.data]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['conversation', projectId, threadId] });
    void queryClient.invalidateQueries({ queryKey: ['conversation-messages', projectId, threadId] });
    void queryClient.invalidateQueries({ queryKey: ['conversation-summary', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['conversations', projectId] });
  };

  // The read cursor: once the newest message is shown, say so — debounced, and
  // only forward (the server keeps the maximum anyway).
  const sentSeq = useRef(0);
  const newest = messages.length > 0 ? (messages[messages.length - 1]?.seq ?? 0) : 0;
  const readSeq = thread.data?.me.readSeq ?? 0;
  useEffect(() => {
    if (newest === 0 || newest <= Math.max(readSeq, sentSeq.current)) return;
    const timer = setTimeout(() => {
      sentSeq.current = newest;
      void api.markConversationRead(projectId, threadId, newest).then(() => {
        void queryClient.invalidateQueries({ queryKey: ['conversation-summary', projectId] });
      });
    }, 800);
    return () => {
      clearTimeout(timer);
    };
  }, [newest, readSeq, projectId, threadId, queryClient]);

  // In the pane, follow the conversation: open on the newest message, and stay
  // there as new ones arrive — unless the reader has scrolled back to read.
  const scroller = useRef<HTMLOListElement>(null);
  const pinned = useRef(true);
  useEffect(() => {
    const box = scroller.current;
    if (layout !== 'pane' || box === null || !pinned.current) return;
    box.scrollTop = box.scrollHeight;
  }, [newest, layout]);

  const act = useMutation({
    mutationFn: async (run: () => Promise<unknown>) => run(),
    onSuccess: refresh,
    onError: (error) => {
      setNotice(error instanceof Error ? error.message : 'That did not work. Try again.');
    },
  });

  const gone =
    (thread.error instanceof ApiError && thread.error.status === 404) ||
    (live.error instanceof ApiError && live.error.status === 404);

  const shown = gone || (thread.data !== undefined && live.data !== undefined);
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;
  useEffect(() => {
    if (shown) onShownRef.current?.();
  }, [shown]);

  if (gone) {
    return (
      <div data-testid="conversation-gone" style={{ display: 'grid', gap: 10 }}>
        <p style={{ margin: 0 }}>This conversation is not available to you any more.</p>
        {onClosed ? (
          <div>
            <button type="button" className="ctl" onClick={onClosed} data-testid="conversation-gone-back">
              Back
            </button>
          </div>
        ) : null}
      </div>
    );
  }
  if (!thread.data) {
    return <p className="mm" data-testid="conversation-loading">Loading the conversation…</p>;
  }

  const t: ConversationThreadView = thread.data;
  const isPrivate = t.visibility === 'PRIVATE';
  const isParticipant = t.me.participant === 'ACTIVE';
  const isOwner = t.me.participantRole === 'OWNER';
  const mayManage =
    canPost &&
    (t.createdByUserId === me ? !isPrivate || isParticipant : isPrivate ? isParticipant : canManage);
  const resolved = t.status === 'RESOLVED';
  const peopleList = people.data?.people;

  const mentionable = isPrivate
    ? mentionablesFrom(peopleList, (p) => t.participantUserIds.includes(p.userId), me)
    : mentionablesFrom(peopleList, (p) => p.canReadAnchorThreads === true, me);

  const menu: MenuItem[] = [
    ...(mayManage && t.title !== null && !renaming
      ? [
          {
            id: 'rename',
            label: 'Rename',
            testId: 'conversation-rename',
            onSelect: () => {
              setRenaming(true);
            },
          },
        ]
      : []),
    ...(isPrivate && isParticipant
      ? [
          {
            id: 'leave',
            label: 'Leave this conversation',
            testId: 'conversation-leave',
            tone: 'danger' as const,
            onSelect: () => {
              act.mutate(async () => {
                await api.leaveConversation(projectId, t.id);
                onClosed?.();
              });
            },
          },
        ]
      : []),
  ];

  return (
    <section
      className="cv-thread"
      data-layout={layout}
      data-testid="conversation-thread"
      aria-labelledby={`conversation-title-${t.id}`}
    >
      <div className="cv-thread-head">
        <div className="cv-thread-headrow">
          <ThreadHeader
            projectId={projectId}
            thread={t}
            locations={locations.data}
            editing={renaming}
            onRename={(title) => {
              setRenaming(false);
              act.mutate(() => api.renameConversation(projectId, t.id, title));
            }}
            onCancelRename={() => {
              setRenaming(false);
            }}
          />
          <div className="cv-thread-actions">
            <label className="cv-follow">
              <span className="sr-only">Notifications</span>
              <select
                data-testid="conversation-follow"
                aria-label="Notifications"
                value={t.me.followState}
                onChange={(event) => {
                  const state = event.target.value as ConversationFollowState;
                  act.mutate(() => api.followConversation(projectId, t.id, state));
                }}
              >
                <option value="FOLLOWING">Following</option>
                <option value="NOT_FOLLOWING">Not following</option>
                <option value="MUTED">Muted</option>
              </select>
            </label>
            {mayManage ? (
              <button
                type="button"
                className="ctl"
                data-testid={resolved ? 'conversation-reopen' : 'conversation-resolve'}
                onClick={() => {
                  act.mutate(() =>
                    resolved ? api.reopenConversation(projectId, t.id) : api.resolveConversation(projectId, t.id),
                  );
                }}
              >
                {resolved ? 'Reopen' : 'Mark resolved'}
              </button>
            ) : null}
            <MoreMenu items={menu} testId="conversation-more" label="Conversation actions" />
          </div>
        </div>

        {isPrivate ? (
          <Participants
            projectId={projectId}
            thread={t}
            people={peopleList}
            me={me}
            canAdd={canPost && isParticipant}
            canRemoveOthers={isParticipant && (isOwner || canModerate)}
            onChange={(run) => {
              act.mutate(run);
            }}
          />
        ) : null}

        {notice ? (
          <p role="alert" data-testid="conversation-notice" className="cv-notice">
            {notice}
          </p>
        ) : null}
      </div>

      <ol
        ref={scroller}
        className="cv-messages"
        data-testid="conversation-messages"
        aria-label="Messages"
        onScroll={(event) => {
          const box = event.currentTarget;
          pinned.current = box.scrollHeight - box.scrollTop - box.clientHeight < 48;
        }}
      >
        {!olderDone && messages.length > 0 && (messages[0]?.seq ?? 1) > 1 ? (
          <li className="cv-earlier">
            <button
              type="button"
              className="ctl"
              data-testid="conversation-load-earlier"
              onClick={() => {
                pinned.current = false;
                const first = messages[0]?.seq ?? 1;
                void api.conversationMessages(projectId, t.id, { before: first }).then((page) => {
                  setOlder((current) => [...page.messages, ...current]);
                  if (page.messages.length === 0 || (page.messages[0]?.seq ?? 1) <= 1) setOlderDone(true);
                });
              }}
            >
              Show earlier messages
            </button>
          </li>
        ) : null}
        {messages.length === 0 && live.data !== undefined ? (
          <li className="mm cv-system" data-testid="conversation-no-messages">
            No messages yet.
          </li>
        ) : null}
        {messages.map((m) =>
          m.kind !== 'MESSAGE' ? (
            <li key={m.seq} className="mm cv-system" data-testid={`conversation-system-${String(m.seq)}`}>
              {systemText(m, peopleList, me)}
            </li>
          ) : (
            <MessageRow
              key={m.seq}
              message={m}
              people={peopleList}
              me={me}
              projectId={projectId}
              locations={locations.data}
              mayHide={canModerate && !isPrivate && m.authorUserId !== me}
              onWithdraw={() => {
                act.mutate(() => api.withdrawConversationMessage(projectId, m.id));
              }}
              onHide={(reason) => {
                act.mutate(() => api.hideConversationMessage(projectId, m.id, reason));
              }}
              onReport={(reasonCode) => {
                act.mutate(async () => {
                  await api.reportConversationMessage(projectId, m.id, { reasonCode });
                  setNotice(null);
                });
              }}
            />
          ),
        )}
      </ol>

      <div className="cv-thread-foot">
        {resolved ? (
          <p className="mm cv-resolved" data-testid="conversation-resolved-note">
            This conversation is resolved.{mayManage ? ' Reopen it to add a message.' : ''}
          </p>
        ) : (
          <Composer
            testId="conversation-composer"
            mentionable={mentionable}
            referenceable={referenceable}
            disabled={!canPost}
            disabledReason="Your role on this production reads conversations without writing in them."
            onSend={async (body) => {
              pinned.current = true;
              await api.postConversationMessage(projectId, t.id, {
                clientMessageId: newRequestKey(),
                body,
              });
              refresh();
            }}
          />
        )}
      </div>
    </section>
  );
}

function ThreadHeader({
  projectId,
  thread: t,
  locations,
  editing,
  onRename,
  onCancelRename,
}: {
  projectId: string;
  thread: ConversationThreadView;
  locations: ConversationLocations | undefined;
  editing: boolean;
  onRename: (title: string) => void;
  onCancelRename: () => void;
}) {
  const [draft, setDraft] = useState(t.title ?? '');
  const stateNote = ANCHOR_STATE_NOTE[t.anchorState];
  // The thing the conversation is about, when it is a place or a script location
  // this reader may see: photograph, what it is to the production, the links out.
  const place =
    t.anchorType === 'PLACE' ? (locations?.places?.find((p) => String(p.locationId) === t.anchorId) ?? null) : null;
  const scriptLocation =
    t.anchorType === 'REQUIREMENT'
      ? (locations?.scriptLocations?.find((s) => s.requirementId === t.anchorId) ?? null)
      : null;

  return (
    <header className="cv-thread-header">
      {editing ? (
        <form
          className="cv-rename"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.trim() !== '') onRename(draft.trim());
            else onCancelRename();
          }}
        >
          <input
            aria-label="Conversation title"
            data-testid="conversation-rename-input"
            value={draft}
            maxLength={CONVERSATION_TITLE_MAX}
            autoFocus
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <button type="submit" className="ctl" data-testid="conversation-rename-save">Save</button>
          <button type="button" className="ctl" data-testid="conversation-rename-cancel" onClick={onCancelRename}>Cancel</button>
        </form>
      ) : (
        <h3 id={`conversation-title-${t.id}`} className="cv-thread-title" data-testid="conversation-title">
          {t.title ?? 'Title removed by ScoutAround'}
        </h3>
      )}
      <div className="cv-thread-meta">
        {/* The card below names a place or script location itself; the label line
            stays for every other anchor, and for the state note. */}
        {place === null && scriptLocation === null ? (
          <span data-testid="conversation-anchor">
            {t.anchorType === 'REFERENCE' && t.anchorState === 'LIVE' ? (
              // A reference's discussion names the reference and opens it in the
              // library, where its preview, links and tags are.
              <Link
                to={referenceInLibraryPath(projectId, t.anchorId)}
                className="cv-anchor-link"
                data-testid="conversation-anchor-reference"
              >
                {t.anchorLabel}
              </Link>
            ) : t.anchorType === 'PROJECT' ? (
              'The whole production'
            ) : (
              t.anchorLabel
            )}
            {stateNote ? (
              <span data-testid="conversation-anchor-state" className="cv-row-note">
                {' · '}
                {stateNote}
              </span>
            ) : null}
          </span>
        ) : stateNote ? (
          <span data-testid="conversation-anchor">
            <span data-testid="conversation-anchor-state" className="cv-row-note">
              {stateNote}
            </span>
          </span>
        ) : null}
        <span data-testid="conversation-visibility">
          <StatusPill state="UNAVAILABLE" size="sm" label={t.visibility === 'PRIVATE' ? 'Private' : 'Public'} />
        </span>
        {t.status === 'RESOLVED' ? (
          <span data-testid="conversation-status">
            <StatusPill state="COMPLETED" size="sm" label="Resolved" />
          </span>
        ) : null}
      </div>
      {place ? <PlacePreview projectId={projectId} place={place} testId="conversation-anchor-place" /> : null}
      {scriptLocation ? (
        <ScriptLocationPreview
          projectId={projectId}
          scriptLocation={scriptLocation}
          places={locations?.places ?? null}
          testId="conversation-anchor-requirement"
        />
      ) : null}
      {t.visibility === 'PRIVATE' ? (
        <p data-testid="conversation-private-notice" className="cv-private-notice">
          {STAFF_ACCESS_NOTICE}
        </p>
      ) : null}
    </header>
  );
}

function Participants({
  projectId,
  thread: t,
  people,
  me,
  canAdd,
  canRemoveOthers,
  onChange,
}: {
  projectId: string;
  thread: ConversationThreadView;
  people: readonly ProjectPerson[] | undefined;
  me: number | null;
  canAdd: boolean;
  canRemoveOthers: boolean;
  onChange: (run: () => Promise<unknown>) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState<number[]>([]);
  const joinable = (people ?? []).filter(
    (p) =>
      p.memberStatus === 'ACTIVE' &&
      p.canReadAnchorThreads === true &&
      !t.participantUserIds.includes(p.userId),
  );

  return (
    <div data-testid="conversation-participants" className="cv-people">
      <span className="cv-people-label">{`People (${String(t.participantUserIds.length)})`}</span>
      <ul className="cv-people-list">
        {t.participantUserIds.map((id, index) => (
          <li key={id} className="cv-person" data-testid={`conversation-participant-${String(id)}`}>
            {personName(people, id, me)}
            {index === 0 ? <span className="mm" style={{ fontSize: 11 }}>(owner)</span> : null}
            {canRemoveOthers && id !== me && index !== 0 ? (
              <button
                type="button"
                className="ctl"
                data-testid={`conversation-participant-remove-${String(id)}`}
                aria-label={`Remove ${personName(people, id, me)}`}
                onClick={() => {
                  onChange(() => api.removeConversationParticipant(projectId, t.id, id));
                }}
                style={{ minHeight: 24, padding: '0 6px', fontSize: 11 }}
              >
                Remove
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {canAdd ? (
        adding ? (
          <div data-testid="conversation-add-people" style={{ display: 'grid', gap: 6, padding: 10, border: '1px solid var(--color-divider)' }}>
            <p data-testid="conversation-add-history-warning" style={{ margin: 0, fontSize: 12.5 }}>
              They will see the whole conversation, including earlier messages. {STAFF_ACCESS_NOTICE}
            </p>
            {joinable.length === 0 ? (
              <p className="mm" style={{ margin: 0, fontSize: 13 }}>Everyone who can see this is already here.</p>
            ) : (
              joinable.map((p) => (
                <label key={p.userId} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    data-testid={`conversation-add-person-${String(p.userId)}`}
                    checked={chosen.includes(p.userId)}
                    onChange={(event) => {
                      setChosen((current) =>
                        event.target.checked ? [...current, p.userId] : current.filter((x) => x !== p.userId),
                      );
                    }}
                  />
                  {p.displayName ?? 'Member'}
                </label>
              ))
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="ctl"
                data-on="1"
                data-testid="conversation-add-people-save"
                disabled={chosen.length === 0}
                onClick={() => {
                  onChange(() => api.addConversationParticipants(projectId, t.id, chosen));
                  setChosen([]);
                  setAdding(false);
                }}
              >
                Add
              </button>
              <button type="button" className="ctl" onClick={() => { setAdding(false); }}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <button type="button" className="ctl" data-testid="conversation-add-people-open" onClick={() => { setAdding(true); }}>
              Add people
            </button>
          </div>
        )
      ) : null}
    </div>
  );
}

function MessageRow({
  message: m,
  people,
  me,
  projectId,
  locations,
  mayHide,
  onWithdraw,
  onHide,
  onReport,
}: {
  message: ConversationMessageView;
  people: readonly ProjectPerson[] | undefined;
  me: number | null;
  projectId: string;
  locations: ConversationLocations | undefined;
  mayHide: boolean;
  onWithdraw: () => void;
  onHide: (reason: string) => void;
  onReport: (reason: ConversationReportReason) => void;
}) {
  const [reporting, setReporting] = useState(false);
  const mine = me !== null && m.authorUserId === me;
  const concealed = m.body === null;

  const actions: MenuItem[] = concealed
    ? []
    : [
        mine
          ? { id: 'withdraw', label: 'Withdraw', testId: `conversation-withdraw-${String(m.seq)}`, onSelect: onWithdraw }
          : {
              id: 'report',
              label: 'Report to ScoutAround',
              testId: `conversation-report-${String(m.seq)}`,
              onSelect: () => {
                setReporting((open) => !open);
              },
            },
        ...(mayHide
          ? [
              {
                id: 'hide',
                label: 'Hide from the production',
                testId: `conversation-hide-${String(m.seq)}`,
                onSelect: () => {
                  onHide('OFF_TOPIC_OR_INAPPROPRIATE');
                },
              },
            ]
          : []),
      ];

  return (
    <li className="cv-message" data-mine={mine ? '1' : undefined} data-testid={`conversation-message-${String(m.seq)}`}>
      <div className="cv-message-head">
        <span className="cv-avatar" aria-hidden="true">
          {initials(personName(people, m.authorUserId, me))}
        </span>
        <strong>{personName(people, m.authorUserId, me)}</strong>
        <span className="mm cv-message-when">{when(m.createdAt)}</span>
        <span className="cv-message-menu">
          <MoreMenu items={actions} testId={`conversation-message-actions-${String(m.seq)}`} label="Message actions" />
        </span>
      </div>
      <div className="cv-message-body">
        {concealed ? (
          <p className="mm" data-testid={`conversation-message-concealed-${String(m.seq)}`} style={{ margin: 0, fontStyle: 'italic' }}>
            {placeholderFor(m)}
          </p>
        ) : (
          <MessageBody
            body={m.body ?? ''}
            people={people}
            me={me}
            projectId={projectId}
            locations={locations}
            testId={`conversation-message-${String(m.seq)}-body`}
          />
        )}
      </div>
      {reporting ? (
        <div data-testid={`conversation-report-form-${String(m.seq)}`} className="cv-report">
          <span style={{ fontSize: 12.5 }}>Report to ScoutAround:</span>
          {ConversationReportReason.options.map((reason) => (
            <button
              key={reason}
              type="button"
              className="ctl"
              data-testid={`conversation-report-${String(m.seq)}-${reason}`}
              onClick={() => {
                onReport(reason);
                setReporting(false);
              }}
              style={{ minHeight: 24, padding: '0 8px', fontSize: 11.5 }}
            >
              {REPORT_LABELS[reason]}
            </button>
          ))}
        </div>
      ) : null}
    </li>
  );
}
